/**
 * Predictive search — modeled on Horizon's predictive-search-component.
 *
 * Differences from Horizon (forced by our infrastructure):
 *   - Native <dialog> instead of <dialog-component>.
 *   - /search/suggest.json instead of Section Rendering API + morph. We
 *     render the dropdown ourselves from the JSON response.
 *   - No "recently viewed" — would need localStorage state management.
 *
 * Public API on <predictive-search-component>:
 *   open()      show the panel (sous le header, non-modal)
 *   close()     hide the panel
 *   toggle()    open/close
 *   reset()     clear input, restore empty state
 */

import { Component } from "@theme/component";
import { debounce } from "@theme/utilities";
import { convertMoneyToMinorUnits, formatMoney } from "@theme/money-formatting";

/** Max items per group. Shopify caps at 10; Horizon uses 4. */
const RESOURCE_LIMIT = 4;

const DEFAULT_STRINGS = {
  no_results: "Aucun résultat.",
  view_all: "Rechercher « %term% »",
  group_queries: "Suggestions",
  group_products: "Produits",
  group_collections: "Collections",
  group_pages: "Pages",
  group_articles: "Articles",
};

class PredictiveSearchComponent extends Component {
  // Only the refs the component genuinely cannot work without are required.
  // closeButton / footer / resetButton are optional and guarded below, so a
  // stale or partial render degrades gracefully instead of throwing.
  requiredRefs = ["dialog", "searchInput", "predictiveSearchResults"];

  /** @type {AbortController | null} */
  #activeFetch = null;

  /** @type {string} Cached server-rendered empty state HTML. */
  #emptyStateHTML = "";

  /** Track whether last navigation was keyboard (for focus styling). */
  #isKeyboardNavigation = false;

  /** @type {string} Active currency ISO code, e.g. "EUR". */
  #currency = "USD";

  /** @type {string} Full Shopify money format, e.g. "{{amount_with_comma_separator}} €" — kept WITH the currency symbol for display. */
  #moneyFormat = "{{amount}}";

  connectedCallback() {
    super.connectedCallback();

    this.#emptyStateHTML = this.refs.predictiveSearchResults.innerHTML;
    this.#currency = this.dataset.currency || "USD";
    this.#moneyFormat = this.dataset.moneyFormat || "{{amount}}";

    document.addEventListener("keydown", this.#onGlobalKeyDown);
    this.refs.dialog.addEventListener("click", this.#onBackdropClick);
    this.refs.dialog.addEventListener("close", this.#onDialogClose);
    this.refs.searchInput.addEventListener("input", this.#onInput);
    this.refs.searchInput.addEventListener("keydown", this.#onSearchKeyDown);
    this.refs.closeButton?.addEventListener("click", () => this.close());
    this.refs.resetButton?.addEventListener("click", () => this.reset());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener("keydown", this.#onGlobalKeyDown);
    this.#activeFetch?.abort();
    // Safety net: if the component is removed while open, don't leave the
    // page stuck with scroll disabled or the header forced opaque.
    if (this.refs.dialog?.open) {
      document.documentElement.style.overflow = "";
      document.documentElement.classList.remove("search-open");
    }
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  open() {
    if (this.refs.dialog.open) return;
    // Non-modal : le panneau s'affiche sous le header (pas de top-layer ni de
    // backdrop natif), positionné en CSS comme le drawer. show() conserve
    // l'API dialog (open / close() / événement 'close').
    this.refs.dialog.show();
    // Verrouille le scroll de l'arrière-plan + force le header opaque tant que
    // la recherche est ouverte. Restaurés sur l'événement 'close' du dialog.
    document.documentElement.style.overflow = "hidden";
    document.documentElement.classList.add("search-open");
    requestAnimationFrame(() => this.refs.searchInput.focus());
  }

  close() {
    if (!this.refs.dialog.open) return;
    this.refs.dialog.close();
  }

  toggle() {
    this.refs.dialog.open ? this.close() : this.open();
  }

  reset() {
    this.refs.searchInput.value = "";
    this.refs.searchInput.setAttribute("aria-expanded", "false");
    this.refs.searchInput.focus();
    this.#hideResetButton();
    this.refs.predictiveSearchResults.innerHTML = this.#emptyStateHTML;
  }

  // ---------------------------------------------------------------------------
  // Input & keyboard
  // ---------------------------------------------------------------------------

  #onInput = debounce(() => {
    const term = this.refs.searchInput.value.trim();

    if (!term) {
      this.#hideResetButton();
      this.refs.searchInput.setAttribute("aria-expanded", "false");
      this.refs.predictiveSearchResults.innerHTML = this.#emptyStateHTML;
      return;
    }

    this.#showResetButton();
    this.#fetchResults(term);
  }, 200);

  #onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close();
      return;
    }

    const items = this.#items;
    const total = items.length;
    if (!total || event.key === "ArrowLeft" || event.key === "ArrowRight") return;

    const current = this.#currentIndex;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current < total - 1 ? current + 1 : 0;
        break;

      case "ArrowUp":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = current > 0 ? current - 1 : total - 1;
        break;

      case "Tab":
        event.preventDefault();
        this.#isKeyboardNavigation = true;
        this.#currentIndex = event.shiftKey ? (current > 0 ? current - 1 : total - 1) : current < total - 1 ? current + 1 : 0;
        break;

      case "Enter": {
        if (current >= 0) {
          event.preventDefault();
          items[current].querySelector("a")?.click();
        }
        // Else let the form submit naturally to /search?q=...
        break;
      }
    }
  };

  /** CMD+K / Ctrl+K toggles the panel from anywhere; Escape closes it. */
  #onGlobalKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "k") {
      event.preventDefault();
      this.toggle();
      return;
    }
    // Un dialog non-modal ne se ferme pas sur Échap nativement → on le gère
    // (utile quand le focus n'est pas dans l'input : résultat, bouton fermer…).
    if (event.key === "Escape" && this.refs.dialog.open) {
      this.close();
    }
  };

  /** Click on the dialog backdrop closes it. */
  #onBackdropClick = (event) => {
    if (event.target === this.refs.dialog) this.close();
  };

  #onDialogClose = () => {
    document.documentElement.style.overflow = "";
    document.documentElement.classList.remove("search-open");
    this.reset();
  };

  // ---------------------------------------------------------------------------
  // Result navigation
  // ---------------------------------------------------------------------------

  get #items() {
    return Array.from(this.refs.predictiveSearchResults.querySelectorAll(".predictive-search-results__item"));
  }

  get #currentIndex() {
    return this.#items.findIndex((el) => el.getAttribute("aria-selected") === "true");
  }

  set #currentIndex(index) {
    const items = this.#items;
    if (!items.length) return;

    items.forEach((item, i) => {
      if (i === index) {
        item.setAttribute("aria-selected", "true");
        if (this.#isKeyboardNavigation) item.classList.add("keyboard-focus");
        item.scrollIntoView({ block: "nearest", behavior: "smooth" });
      } else {
        item.removeAttribute("aria-selected");
        item.classList.remove("keyboard-focus");
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Fetching & rendering
  // ---------------------------------------------------------------------------

  async #fetchResults(term) {
    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    const url = new URL(`${window.Shopify.routes.root}search/suggest.json`, window.location.origin);
    url.searchParams.set("q", term);
    url.searchParams.set("resources[limit]", String(RESOURCE_LIMIT));
    url.searchParams.set("resources[limit_scope]", "each");
    // Colonne gauche = produits + collections qui matchent ; colonne droite =
    // product cards. On ne demande donc que ces deux types (réponse plus légère).
    url.searchParams.set("resources[type]", "product,collection");
    url.searchParams.set("resources[options][unavailable_products]", "last");
    url.searchParams.set("resources[options][fields]", "title,product_type,variants.title,vendor");

    try {
      const response = await fetch(url.toString(), { signal: this.#activeFetch.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();

      this.#render(data.resources?.results ?? {}, term);
      this.refs.searchInput.setAttribute("aria-expanded", "true");
    } catch (error) {
      if (error.name === "AbortError") return;
      console.error("[predictive-search] fetch failed:", error);
      this.refs.predictiveSearchResults.innerHTML = this.#emptyStateHTML;
    } finally {
      this.#activeFetch = null;
    }
  }

  #render(results, term) {
    const products = (results.products ?? []).slice(0, RESOURCE_LIMIT);
    const collections = (results.collections ?? []).slice(0, RESOURCE_LIMIT);

    if (products.length === 0 && collections.length === 0) {
      this.refs.predictiveSearchResults.innerHTML = `
        <p class="predictive-search__no-results">${this.#text("no_results")}</p>`;
      return;
    }

    // Colonne gauche "Suggestion" : liste texte des produits + collections qui
    // contiennent le terme. Colonne droite : grille de product cards (produits)
    // + bouton "voir tous les résultats".
    const suggestionItems = [...products, ...collections].map((r) => this.#renderSuggestionItem(r)).join("");
    const productCards = products.map((p) => this.#renderProductCard(p)).join("");
    const viewAllLabel = this.#text("view_all").replace("%term%", this.#escape(term));

    this.refs.predictiveSearchResults.innerHTML = `
      <div class="predictive-search-results">
        <div class="predictive-search-results__col predictive-search-results__col--suggestions">
          <h3 class="predictive-search-results__group-title">${this.#text("group_queries")}</h3>
          ${suggestionItems ? `<ul class="predictive-search-results__list" role="list">${suggestionItems}</ul>` : ""}
        </div>
        <div class="predictive-search-results__col predictive-search-results__col--products">
          ${productCards ? `<ul class="predictive-search-results__products" role="list">${productCards}</ul>` : ""}
          <button type="submit" class="predictive-search__search-button btn btn-primary">${viewAllLabel}</button>
        </div>
      </div>`;
  }

  // Élément texte de la colonne gauche : produit OU collection (titre + lien).
  #renderSuggestionItem(resource) {
    const url = this.#escape(resource.url || "#");
    const title = this.#escape(resource.title || resource.text || "");
    return `
      <li class="predictive-search-results__item" role="option">
        <a href="${url}">${title}</a>
      </li>`;
  }

  #renderProductCard(resource) {
    const url = this.#escape(resource.url || "#");
    const title = this.#escape(resource.title || "");
    const img = this.#imageUrlWithWidth(resource.featured_image?.url || resource.image, 400);
    const imgTag = img ? `<img src="${this.#escape(img)}" alt="" loading="lazy">` : "";

    const price = resource.price != null ? this.#formatMoney(resource.price) : null;
    // Comparaison numérique (chaînes en unité majeure type "790.00").
    const hasDiscount = resource.compare_at_price != null && Number(resource.compare_at_price) > Number(resource.price);
    const compareAt = hasDiscount ? this.#formatMoney(resource.compare_at_price) : null;
    const priceHtml = price ? `<span class="predictive-search-card__price">${compareAt ? `<s>${compareAt}</s> ` : ""}${price}</span>` : "";

    return `
      <li class="predictive-search-results__item" role="option">
        <a class="predictive-search-card" href="${url}">
          <span class="predictive-search-card__image">${imgTag}</span>
          <span class="predictive-search-card__info">
            <span class="predictive-search-card__title">${title}</span>
            ${priceHtml}
          </span>
        </a>
      </li>`;
  }

  // ---------------------------------------------------------------------------
  // UI helpers
  // ---------------------------------------------------------------------------

  #showResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = false;
  }
  #hideResetButton() {
    if (this.refs.resetButton) this.refs.resetButton.hidden = true;
  }

  #text(key) {
    const camel = key.replace(/_(\w)/g, (_, c) => c.toUpperCase());
    return this.dataset[`text${camel.charAt(0).toUpperCase()}${camel.slice(1)}`] || DEFAULT_STRINGS[key] || key;
  }

  #escape(str) {
    return String(str).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
  }

  /**
   * Appends/replaces the Shopify CDN `width` param so the served image is
   * resized to the requested width (e.g. 400px) instead of the full-size file.
   * @param {string} url
   * @param {number} width
   * @returns {string}
   */
  #imageUrlWithWidth(url, width) {
    if (!url) return url;
    if (/([?&])width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return url + (url.includes("?") ? "&" : "?") + `width=${width}`;
  }

  /**
   * Formats a price from /search/suggest.json. The API returns prices in the
   * MAJOR currency unit as a string (e.g. "790.00"), NOT cents — so we parse it
   * to minor units and run it through the theme's money formatter, matching the
   * `| money` Liquid filter (currency symbol, separators, precision).
   * @param {string|number} price
   * @returns {string}
   */
  #formatMoney(price) {
    const minorUnits = convertMoneyToMinorUnits(String(price), this.#currency);
    if (minorUnits == null) return "";
    return formatMoney(minorUnits, this.#moneyFormat, this.#currency);
  }
}

if (!customElements.get("predictive-search-component")) {
  customElements.define("predictive-search-component", PredictiveSearchComponent);
}
